import { Box, CircularProgress, Stack, Typography } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { LoadingButton } from '@mui/lab';
import { formatDollarAmount } from '../../utils/global/helper';
import { CheckoutSummaryItem } from '../../types/checkouts';
import { CheckoutPricing } from '../../hooks/checkouts/useCheckoutPricing';

interface Props {
  summary: CheckoutPricing['summary'];
  submitText: string;
  disabled: boolean;
  isSubmitting: boolean;
  onSubmit: () => void;
  footNote?: string;
}

function RowAction({
  action,
}: {
  action: NonNullable<CheckoutSummaryItem['floatingButton']>;
}) {
  // The rail uses the design's ✕ for removals; the row data carries the
  // classic table's trash can.
  const Icon = action.icon === DeleteIcon ? CloseRoundedIcon : action.icon;
  return (
    <Box
      component="button"
      type="button"
      aria-label="Edit line"
      onClick={action.onClick}
      sx={{
        width: 26,
        height: 26,
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 0,
        border: 0,
        borderRadius: '5px',
        background: 'transparent',
        color: 'var(--outlineGrey)',
        cursor: 'pointer',
        '&:hover': {
          background: 'var(--lineGrey)',
          color: theme => theme.palette.common.black,
        },
      }}>
      <Icon sx={{ fontSize: '0.9375rem' }} />
    </Box>
  );
}

function SummaryRow({
  item,
  isLoading,
}: {
  item: CheckoutSummaryItem;
  isLoading?: boolean;
}) {
  const isNegative = item.value < 0;
  const showAction =
    !isLoading &&
    !!item.floatingButton &&
    (item.floatingButton.showWithNoValue || !!item.value);

  return (
    <Stack
      direction="row"
      alignItems="center"
      gap={1.25}
      sx={{
        padding: '11px 0',
        borderBottom: '1px solid var(--lineGrey)',
      }}>
      {!item.value && item.cellActionWhenEmpty ? (
        <Box
          component="button"
          type="button"
          onClick={item.cellActionWhenEmpty.onClick}
          sx={{
            padding: 0,
            border: 0,
            background: 'transparent',
            cursor: 'pointer',
            color: theme => theme.palette.info.main,
            typography: 'h5',
            fontWeight: 700,
          }}>
          {item.cellActionWhenEmpty.label}
        </Box>
      ) : (
        <>
          <Typography variant="subtitle2" sx={{ color: 'var(--darkGrey)' }}>
            {item.label}
          </Typography>
          {item.isPaid && (
            <Typography
              variant="caption"
              fontWeight={600}
              sx={{ color: theme => theme.palette.secondary.main }}>
              (paid)
            </Typography>
          )}
          <Typography
            variant="subtitle2"
            marginLeft="auto"
            sx={{
              color: theme =>
                item.valueColor ??
                (isNegative || item.isPaid
                  ? theme.palette.primary.main
                  : 'var(--darkGrey)'),
            }}>
            {isLoading ? '-' : formatDollarAmount(item.value)}
          </Typography>
        </>
      )}
      {showAction && <RowAction action={item.floatingButton!} />}
    </Stack>
  );
}

export default function CheckoutOrderSummary({
  summary: {
    preSubtotalItems,
    postSubtotalItems,
    subtotal,
    total,
    isLoading,
    minimumPrice,
  },
  submitText,
  disabled,
  isSubmitting,
  onSubmit,
  footNote,
}: Props) {
  const visible = (items: CheckoutSummaryItem[]) =>
    items.filter(item => item.visible ?? true);

  return (
    <>
      <Typography
        variant="caption"
        component="p"
        fontWeight={600}
        sx={{ color: theme => theme.palette.primary.main }}>
        Order summary
      </Typography>

      <Box className="checkout-summary-rows">
        {visible(preSubtotalItems).map(item => (
          <SummaryRow key={item.label} item={item} isLoading={isLoading} />
        ))}

        <Stack
          direction="row"
          justifyContent="space-between"
          sx={{
            padding: '12px 0',
            borderBottom: '1px solid var(--lineGrey)',
          }}>
          <Typography variant="h5">Subtotal</Typography>
          <Typography variant="h5">
            {isLoading ? '-' : formatDollarAmount(subtotal)}
          </Typography>
        </Stack>

        {visible(postSubtotalItems).map(item => (
          <SummaryRow key={item.label} item={item} isLoading={isLoading} />
        ))}

        <Stack
          direction="row"
          alignItems="baseline"
          justifyContent="space-between"
          gap={2}
          paddingTop={2}>
          <Typography variant="h3">Total due</Typography>
          {isLoading ? (
            <CircularProgress size={20} color="primary" />
          ) : (
            <Typography variant="h1" component="p">
              {formatDollarAmount(total)}
            </Typography>
          )}
        </Stack>

        {minimumPrice?.errorVisible && (
          <Typography
            variant="caption"
            component="p"
            fontWeight={600}
            textAlign="right"
            paddingTop={1}
            sx={{ color: theme => theme.palette.error.dark }}>
            Enter a price greater than {formatDollarAmount(minimumPrice.value)}
          </Typography>
        )}
      </Box>

      <Stack marginTop="auto" gap={1.5}>
        <LoadingButton
          variant="contained"
          className="primary"
          fullWidth
          disabled={disabled}
          loading={isSubmitting}
          onClick={onSubmit}
          sx={{
            height: 60,
            borderRadius: '50px',
            typography: 'h3',
          }}>
          {submitText}
        </LoadingButton>
        {!!footNote && (
          <Typography
            variant="caption"
            fontWeight={600}
            textAlign="center"
            sx={{ color: 'text.tertiary' }}>
            {footNote}
          </Typography>
        )}
      </Stack>
    </>
  );
}
